import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Bell, CheckCircle2, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const sevStyle = {
  critical: { ring: "border-rose-200", icon: "bg-rose-50 text-rose-600", bar: "bg-rose-500" },
  warning: { ring: "border-amber-200", icon: "bg-amber-50 text-amber-600", bar: "bg-amber-400" },
  info: { ring: "border-neutral-200", icon: "bg-neutral-50 text-neutral-600", bar: "bg-neutral-500" },
};

export default function Alerts() {
  const { alerts, resolveAlert } = useFgp();
  const [filter, setFilter] = useState("All");

  const filtered = filter === "All" ? alerts : alerts.filter((a) => a.severity === filter);
  const counts = {
    critical: alerts.filter((a) => a.severity === "critical").length,
    warning: alerts.filter((a) => a.severity === "warning").length,
    info: alerts.filter((a) => a.severity === "info").length,
  };

  return (
    <div>
      <PageHeader title="Alerts" subtitle="Everything that needs your attention, prioritised by urgency." />

      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { k: "critical", label: "Critical", icon: AlertTriangle, tone: "text-rose-600 bg-rose-50" },
          { k: "warning", label: "Warnings", icon: Clock, tone: "text-amber-600 bg-amber-50" },
          { k: "info", label: "Informational", icon: Bell, tone: "text-neutral-600 bg-neutral-50" },
        ].map((c) => (
          <div key={c.k} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${c.tone}`}><c.icon className="h-5 w-5" /></div>
            <div><p className="font-heading text-2xl font-semibold text-slate-900">{counts[c.k]}</p><p className="text-xs text-slate-400">{c.label}</p></div>
          </div>
        ))}
      </div>

      <div className="flex gap-1.5 mb-4">
        {["All", "critical", "warning", "info"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`alert-filter-${s}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${filter === s ? "bg-neutral-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 rounded-xl border border-dashed border-slate-200 bg-slate-50/50" data-testid="alerts-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-slate-700">All clear</p>
            <p className="text-sm text-slate-400">No alerts need your attention right now.</p>
          </div>
        )}
        {filtered.map((a, i) => {
          const s = sevStyle[a.severity];
          return (
            <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className={`bg-white rounded-xl border ${s.ring} shadow-sm overflow-hidden flex`} data-testid={`alert-${a.id}`}>
              <div className={`w-1 ${s.bar}`} />
              <div className="flex items-start gap-4 p-5 flex-1">
                <span className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.icon}`}><AlertTriangle className="h-4 w-4" /></span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800">{a.title}</p>
                    <StatusBadge status={a.severity} />
                    <span className="text-xs text-slate-300">·</span>
                    <span className="text-xs text-slate-400">{a.category}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{a.detail}</p>
                  <p className="text-xs text-slate-400 mt-2">{a.time}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => { resolveAlert(a.id); toast.success("Alert resolved"); }} className="gap-1.5 flex-shrink-0" data-testid={`resolve-${a.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Resolve
                </Button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
